// Story 7.1 — TanStack Query mutation for POST /api/v1/auth/register.
//
// FIRST SPA consumer of the Story 1.9 register endpoint. Backend body
// is `{ email, password, tos_version_label }` (a STRING like "v1.0",
// NOT a UUID) — the SPA fetches the label from useCurrentTos and
// forwards it verbatim.
//
// On 201 success the backend does NOT set a session cookie. Verify-
// email must complete out-of-band before login works. The consumer
// redirects to /verify-email-pending on success.

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface CreateAccountVariables {
  email: string;
  password: string;
  tos_version_label: string;
  /** Closed-beta gate: required when GET /auth/signup-policy says
   *  access_code_required. Omitted entirely when signup is open. */
  access_code?: string;
  /** Story 6.18 (follow-up F7) — the token the hCaptcha widget produced.
   *  Omitted entirely when the widget has not been solved, because the
   *  decision is the BACKEND's: it verifies the token server-side whenever
   *  HCAPTCHA_SECRET is set and ignores it when it is not. A client-side
   *  "you must solve the captcha" check is a hint, never a gate. */
  hcaptcha_token?: string;
}

export interface CreateAccountResponseData {
  user_id: string;
  /** TRUE when the backend created the account already verified, so the SPA
   *  skips /verify-email-pending and signs the user straight in. Set by
   *  either backend environment toggle: the beta signup posture
   *  (SIGNUP_EMAIL_VERIFICATION=dismissed, owner requirement 2026-09-03) or
   *  local mock mode (GREENHOLD_MOCK_EXTERNALS). Absent/false wherever
   *  verification is required — which is always, in production, because the
   *  backend refuses to boot with either toggle set there. */
  email_verified?: boolean;
}

export type CreateAccountErrorCode =
  | "EMAIL_ALREADY_REGISTERED"
  | "VALIDATION_FAILED"
  | "RATE_LIMITED"
  // 403 — closed-beta gate: access code missing or wrong.
  | "invalid_access_code"
  | "CREATE_ACCOUNT_FAILED";

export interface CreateAccountError {
  code: CreateAccountErrorCode | string;
  message: string;
  field?: "email" | "password" | "tos_version_label" | string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCreateAccount(
  variables: CreateAccountVariables,
): Promise<CreateAccountResponseData> {
  const response = await fetch("/api/v1/auth/register", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreateAccountError = {
      code: "CREATE_ACCOUNT_FAILED",
      message: `Registration failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as CreateAccountResponseData;
}

export function useCreateAccount(): UseMutationResult<
  CreateAccountResponseData,
  CreateAccountError,
  CreateAccountVariables
> {
  return useMutation<
    CreateAccountResponseData,
    CreateAccountError,
    CreateAccountVariables
  >({
    mutationFn: postCreateAccount,
    retry: false,
  });
}
